import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Pages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FarmerPartnershipPage() {
  const [formData, setFormData] = useState({
    full_name: '',
    farm_name: '',
    location: '',
    crop: 'Ashwagandha',
    cultivation_area: '',
    expected_quantity: '',
    harvest_period: '',
    phone: '',
    email: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const sopSteps = [
    { step: 1, title: "Farmer Registration", desc: "Submit application with farm details" },
    { step: 2, title: "Farm Verification", desc: "On-site visit and documentation review" },
    { step: 3, title: "Quality Assessment", desc: "Evaluate cultivation practices and soil quality" },
    { step: 4, title: "Sample Testing", desc: "Laboratory analysis of product samples" },
    { step: 5, title: "Contract Agreement", desc: "Terms finalization and agreement signing" },
    { step: 6, title: "Harvest Monitoring", desc: "Regular quality checks during cultivation" },
    { step: 7, title: "Final Inspection", desc: "Pre-procurement quality verification" },
    { step: 8, title: "Procurement", desc: "Purchase and payment processing" }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/farmer-partnership`, formData);
      setSubmitStatus({
        type: 'success',
        message: response.data.message
      });
      // Reset form
      setFormData({
        full_name: '',
        farm_name: '',
        location: '',
        crop: 'Ashwagandha',
        cultivation_area: '',
        expected_quantity: '',
        harvest_period: '',
        phone: '',
        email: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to submit your application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container farmer-partnership-page">
      {/* Header */}
      <header className="page-header">
        <div className="container">
          <Link to="/" className="back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Home
          </Link>
          <div className="logo-small">
            <span className="logo-icon">🌿</span>
            <span>HOPE INTERNATIONAL</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="farmer-hero">
        <div className="container">
          <h1>Partner With HOPE INTERNATIONAL</h1>
          <p className="hero-subtitle">Farmer Sourcing Program</p>
        </div>
      </section>

      {/* Main Content */}
      <main className="farmer-main-content">
        <div className="container">
          {/* Introduction */}
          <section className="farmer-intro">
            <p>
              HOPE INTERNATIONAL works directly with selected farmers to source export-grade Ashwagandha. 
              Our sourcing process follows strict quality, traceability, and compliance standards to meet 
              international buyer requirements.
            </p>
          </section>

          {/* SOP Section */}
          <section className="sop-section">
            <h2>Our Sourcing Process</h2>
            <p className="sop-intro">Our 8-step Standard Operating Procedure ensures quality, traceability, and compliance at every stage.</p>
            
            <div className="sop-steps-grid">
              {sopSteps.map((item, index) => (
                <div key={item.step} className="sop-step-card">
                  <div className="step-number">{item.step}</div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                  {index < sopSteps.length - 1 && <div className="step-connector"></div>}
                </div>
              ))}
            </div>
          </section>

          {/* Important Note */}
          <div className="important-note">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p>
              <strong>Note:</strong> Submission does not guarantee procurement. All sourcing is subject to 
              quality evaluation and trial approval.
            </p>
          </div>

          {/* Application Form */}
          <section className="farmer-form-section" id="apply">
            <h2>Apply as a Farmer Partner</h2>
            <p className="form-intro">
              Fill out the form below to express your interest in partnering with Hope International. 
              Our sourcing team will review your application and contact you if your profile matches our requirements.
            </p>

            {submitStatus && (
              <div className={`form-status ${submitStatus.type}`}>
                {submitStatus.type === 'success' ? (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                )}
                <p>{submitStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="farmer-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="full_name">Full Name *</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="farm_name">Farm / Firm Name</label>
                  <input
                    type="text"
                    id="farm_name"
                    name="farm_name"
                    value={formData.farm_name}
                    onChange={handleChange}
                    placeholder="Enter farm or firm name"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="location">Location (Village, District, State) *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Rewa, Madhya Pradesh"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="crop">Crop *</label>
                  <select
                    id="crop"
                    name="crop"
                    value={formData.crop}
                    onChange={handleChange}
                    required
                  >
                    <option value="Ashwagandha">Ashwagandha</option>
                    <option value="Moringa">Moringa</option>
                    <option value="Turmeric">Turmeric</option>
                    <option value="Ginger">Ginger</option>
                    <option value="Amla">Amla</option>
                    <option value="Neem">Neem</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="cultivation_area">Cultivation Area *</label>
                  <input
                    type="text"
                    id="cultivation_area"
                    name="cultivation_area"
                    value={formData.cultivation_area}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 5 acres or 2 hectares"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="expected_quantity">Expected Quantity (MT) *</label>
                  <input
                    type="text"
                    id="expected_quantity"
                    name="expected_quantity"
                    value={formData.expected_quantity}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 2 MT per harvest"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="harvest_period">Harvest Period *</label>
                  <input
                    type="text"
                    id="harvest_period"
                    name="harvest_period"
                    value={formData.harvest_period}
                    onChange={handleChange}
                    required
                    placeholder="e.g., January - March"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email ID *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="message">Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Any additional information about your farm, experience, or organic certification status..."
                />
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </form>
          </section>

          {/* Compliance Footer */}
          <div className="compliance-footer">
            <p>
              HOPE INTERNATIONAL follows a compliance-first sourcing approach aligned with 
              export quality standards.
            </p>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="page-footer">
        <div className="container">
          <p>&copy; 2019-2025 Hope International. All Rights Reserved.</p>
          <p className="footer-contact">
            <a href="mailto:Abhishek@hopeinternational.in">Abhishek@hopeinternational.in</a>
            {' | '}
            <a href="tel:+916267592923">+91 6267592923</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default FarmerPartnershipPage;
